import { STATUS_GLYPHS } from '../status';
import type { Weapon } from './weaponFamilies';
import { REALMS_TO_COME } from '../cultivation';
import { ELEMENT_INFO, type DamageType, type Element } from './elements';
import { MOUNT_GLYPHS } from './mounts';
import { LADDER_GLYPHS } from './ladder';
import { ARENA_GLYPHS } from './arena';
import { EXTRA_ARTS, EXTRA_SKILLS } from './arts';
import { DRAGON_PALM_ART_DEF, DRAGON_PALM_SKILLS } from './dragonPalm';
import { STAFF_ART_DEF, STAFF_SKILLS } from './staffArt';
import { HIDDEN_SKILLS } from './hiddenWeapons';
import { DUNGEON_SKILLS } from './dungeon';
import { WORLD_BOSS_SKILLS } from './worldBosses';
import { ZHUMING_NAME } from './layouts/zhuming';
import { LOCK_AIM_FORCED, LOCK_FORMS, WINDUP_DATA, type LockFormDef } from './lockForms';

/**
 * The combat triangle: attack (ท่าแท้) beats break (interrupts its wind-up), break (ท่าทำลาย) beats
 * block (shatters the guard), block beats attack (absorbs it and staggers the attacker).
 * Power moves (ท่าพลัง) spend rage and cannot be blocked; buff moves (ท่าเสริม) act on the caster.
 */
export type SkillKind = 'attack' | 'break' | 'power' | 'heal' | 'buff';

export const SKILL_KIND_LABELS: Readonly<Record<SkillKind, string>> = {
  attack: 'ท่าแท้',
  break: 'ท่าทำลาย',
  power: 'ท่าพลัง',
  heal: 'ท่าฟื้นฟู',
  buff: 'ท่าเสริม',
};

/** Rage (ความโกรธ) pool for power moves. */
/** Rage builds only from blows taken (blocked or not); landing your own hits does not add any. */
export const RAGE = { max: 30, perHitTaken: 4, decayPerSecond: 2 } as const;

export type SkillFx =
  | 'bolt'
  | 'pierce'
  | 'nine'
  | 'calm'
  | 'palm'
  | 'grab'
  | 'whirl'
  | 'breath'
  | 'bite'
  | 'slash'
  | 'crush'
  | 'cleave'
  | 'ring'
  | 'wind'
  | 'taiji'
  | 'snow'
  | 'snowdash'
  | 'blizzard'
  | 'snowstep'
  | 'icebreak'
  | 'snowpride'
  | 'snowblink'
  | 'vajra'
  | 'quake'
  | 'bell'
  | 'sabre'
  | 'chain'
  | 'aura'
  | 'shadow'
  | 'needles'
  | 'brush'
  | 'ink'
  | 'seal'
  | 'blood'
  | 'bloodstorm'
  // ถ้ำคลื่นคำราม's own (render3d/fx/skills/dg*.ts)
  | 'dgWhirlpool'
  | 'dgIron'
  | 'dgHarpoon'
  | 'dgChain'
  | 'dgSleep'
  // Skills v2 (docs/systems/SKILLS_V2.md 3.1; render3d/fx/skills/flyingSwords.ts)
  | 'flyingSwords';

export interface SkillDef {
  readonly id: string;
  /** A lock move (ท่าล็อกเป้า): walks to its target when pressed out of reach, and once it connects both fighters play a paired form (data/lockForms.ts). */
  readonly lockForm?: LockFormDef;
  readonly name: string;
  /** Single Han character shown on the hotbar. */
  readonly glyph: string;
  readonly kind: SkillKind;
  readonly qi: number;
  readonly cooldown: number;
  /** Seconds of telegraphed charge before the skill lands. Break skills always have one. */
  readonly windup: number;
  /** Duel only: seconds after the move lands in which the fighter cannot guard, dodge or press a move (data/moveTiming.ts has the default by kind and weight). */
  readonly recovery?: number;
  readonly range: number;
  readonly power: number;
  /** Stun applied when this break skill shatters a guard. */
  readonly stun?: number;
  /** Hits every hostile within this radius around the caster instead of one target. */
  readonly aoe?: number;
  /** Number of homing projectiles split across hostiles in range. */
  readonly volley?: number;
  /** Fraction of max HP restored over 3 seconds. */
  readonly healPct?: number;
  /** Ground circle placed under the target that detonates after `delay` seconds. */
  readonly telegraph?: { readonly radius: number; readonly delay: number };
  /** Rage spent (power moves). */
  readonly rage?: number;
  /** Status put on the target when the hit lands (not when blocked or dodged). */
  readonly applies?: { readonly status: string; readonly stacks?: number };
  /** Status put on the caster when the move is used. */
  readonly self?: { readonly status: string; readonly stacks?: number };
  /** Removes movement-limiting debuffs and stuns from the caster; usable while stunned. */
  readonly cleanse?: boolean;
  /** Closes in on the target before striking, or dashes forward through everyone in the way. */
  readonly dash?: 'target' | 'forward';
  /** Spends one stack of a status on the caster for extra power (+bonus × power). */
  readonly empoweredBy?: { readonly status: string; readonly bonus: number };
  /** Extra power per stack of a status on the target, which the hit then consumes. */
  readonly perTargetStack?: { readonly status: string; readonly bonus: number };
  /**
   * Ground-targeted: the move lands on a spot the caster picks within `range`, striking everyone
   * within `aoe` of it (instead of around the caster).
   */
  readonly ground?: boolean;
  /**
   * Ground moves: seconds from cast to impact (long-moves.md 3.2, 0.45-0.6). The cast stays open this long with `cast.at`
   * set, so the landing mark shows to both sides; a hit on the caster cancels it. Effective wind-up = max(windup, land).
   */
  readonly land?: number;
  /**
   * Whirlwind: the caster spins for `duration` seconds whether or not anyone is near, striking
   * every foe within `aoe` each `tick` seconds. They can still walk, but do nothing else.
   */
  readonly spin?: { readonly duration: number; readonly tick: number };
  /** Number of strikes, 0.15 s apart. */
  readonly hits?: number;
  /** Stun on a landed hit (first strike only). */
  readonly hitStun?: number;
  /** Status put on the target when this break move shatters its guard. */
  readonly onGuardBreak?: { readonly status: string };
  /** Fraction of damage dealt returned to the caster as HP. */
  readonly lifesteal?: number;
  /**
   * Warp chain: the caster vanishes and reappears beside a hostile within `aoe`, slashing it, up
   * to `hits` times. Each foe is cut once (nearest next) before any is cut again; with no one else
   * left in reach it keeps cutting the same foe. The value is the seconds between warps.
   */
  readonly blink?: number;
  /**
   * Scripted: the World runs only the wind-up and emits the \`skill\` event; the system that owns the move resolves its
   * shape (a cone, a ring, a line, a pull…) from that event (systems/dungeon.ts).
   */
  readonly scripted?: boolean;
  /** Armour: while it winds up the caster is neither interrupted (attack beats break) nor stunned out of it. */
  readonly armour?: boolean;
  /** How it aims (docs/systems/SKILLS_V2.md 1.1). Missing: derived by `aimOf` (data/skillAim.ts). */
  readonly aim?: 'lock' | 'free' | 'ground' | 'volley';
  /** Volleys: flying swords curve to their target (`soft`), needles fly straight (`none`). Missing: `homingOf`. */
  readonly homing?: 'soft' | 'none';
  readonly fx: SkillFx;
  readonly desc: string;
}

const list: SkillDef[] = [
  // ── เพลงกระบี่ร่องศิลา ──
  {
    id: 'ns-thunder',
    name: 'กระบี่อัสนีฟาด',
    glyph: '雷',
    kind: 'attack',
    qi: 12,
    cooldown: 3,
    windup: 0,
    range: 4.5,
    power: 2.2,
    fx: 'bolt',
    desc: 'เรียกสายฟ้าฟาดเป้าหมายจากระยะไกล ใช้ขัดจังหวะศัตรูที่กำลังชาร์จท่าทำลายได้',
  },
  {
    id: 'ns-pierce',
    name: 'กระบี่ทะลวงปราการ',
    glyph: '穿',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.5,
    range: 2,
    power: 1.5,
    stun: 1.8,
    fx: 'pierce',
    desc: 'ชาร์จครึ่งวินาทีแล้วแทงทะลวง หากศัตรูตั้งรับอยู่ การ์ดจะแตกและมึนงง',
  },
  {
    id: 'ns-nine',
    name: 'ร่องศิลาพิฆาตมาร',
    glyph: '九',
    kind: 'attack',
    qi: 35,
    cooldown: 12,
    windup: 0,
    range: 6,
    power: 1.0,
    volley: 9,
    fx: 'nine',
    desc: 'อัญเชิญกระบี่ปราณเก้าเล่ม พุ่งเข้าใส่ศัตรูทุกตัวในระยะ 6 ช่อง',
  },
  {
    id: 'ns-calm',
    name: 'จิตกระบี่สงบนิ่ง',
    glyph: '心',
    kind: 'heal',
    qi: 0,
    cooldown: 18,
    windup: 0,
    range: 0,
    power: 0,
    healPct: 0.35,
    fx: 'calm',
    desc: 'รวมจิตเป็นหนึ่ง ฟื้นพลังชีวิต 35% ในสามวินาที',
  },
  // ── ฝ่ามือเมฆาคล้อย ──
  {
    id: 'cp-wave',
    name: 'ฝ่ามือคลื่นซัด',
    glyph: '掌',
    kind: 'attack',
    qi: 8,
    cooldown: 2.2,
    windup: 0,
    range: 2,
    power: 2.67,
    fx: 'palm',
    desc: 'ซัดฝ่ามือระยะประชิด เร็วและกินลมปราณน้อย',
  },
  {
    id: 'cp-seize',
    name: 'หัตถ์ปลดเกราะ',
    glyph: '擒',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.4,
    range: 2,
    power: 1.93,
    stun: 2.2,
    fx: 'grab',
    desc: 'ท่าทำลายที่ชาร์จเร็วที่สุด คว้าแขนศัตรูให้การ์ดหลุดและมึนงงนาน',
  },
  {
    id: 'cp-storm',
    name: 'ฝ่ามือพายุหมุน',
    glyph: '旋',
    kind: 'attack',
    qi: 22,
    cooldown: 8,
    windup: 0,
    range: 0,
    power: 2.36,
    aoe: 2.2,
    fx: 'whirl',
    desc: 'หมุนตัวปล่อยฝ่ามือรอบทิศ โดนศัตรูทุกตัวในรัศมี 2 ช่อง',
  },
  {
    id: 'cp-breath',
    name: 'ลมหายใจเมฆา',
    glyph: '息',
    kind: 'heal',
    qi: 0,
    cooldown: 14,
    windup: 0,
    range: 0,
    power: 0,
    healPct: 0.25,
    fx: 'breath',
    desc: 'ปรับลมหายใจ ฟื้นพลังชีวิต 25% ในสามวินาที',
  },
  // ── สำนักยอดหมอก: กระบี่ไผ่ห่มหมอก ──
  {
    id: 'bs-gust',
    name: 'ใบไผ่ปลายกระบี่',
    glyph: '風',
    kind: 'attack',
    qi: 8,
    cooldown: 2,
    windup: 0,
    range: 2.2,
    power: 1.63,
    fx: 'wind',
    desc: 'แทงกระบี่เร็วดุจสายลม ระยะไกลกว่ากระบี่ทั่วไป คูลดาวน์สั้น',
  },
  {
    id: 'bs-cloud',
    name: 'ลมทะลวงเมฆ',
    glyph: '刺',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.45,
    range: 2,
    power: 1.34,
    stun: 1.6,
    fx: 'pierce',
    desc: 'ชาร์จสั้นแล้วแทงทะลุการตั้งรับ ศัตรูที่ตั้งรับอยู่จะการ์ดแตกและมึนงง',
  },
  {
    id: 'bs-sweep',
    name: 'กวาดใบไผ่',
    glyph: '掃',
    kind: 'attack',
    qi: 16,
    cooldown: 6,
    windup: 0,
    range: 5,
    ground: true,
    land: 0.5,
    power: 1.25,
    aoe: 1.9,
    fx: 'whirl',
    desc: 'หมุนกระบี่กวาดรอบตัวดุจลมพัดป่าไผ่ โดนศัตรูทุกตัวในรัศมี 2 ช่อง',
  },
  {
    id: 'bs-bamboo',
    name: 'ลมหายใจไผ่เขียว',
    glyph: '竹',
    kind: 'heal',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 0,
    power: 0,
    healPct: 0.2,
    fx: 'breath',
    desc: 'ปรับลมหายใจให้เบาดุจใบไผ่ ฟื้นพลังชีวิต 20% ในสามวินาที',
  },
  // ── สำนักยอดหมอก: หมัดหมอกวน ──
  {
    id: 'tf-push',
    name: 'ผลักหมอกคืนฝั่ง',
    glyph: '推',
    kind: 'attack',
    qi: 8,
    cooldown: 2.5,
    windup: 0,
    range: 2,
    power: 2.09,
    fx: 'palm',
    desc: 'ใช้ความอ่อนผลักแรงศัตรูกลับ ขัดจังหวะท่าทำลายได้ดี',
  },
  {
    id: 'tf-grasp',
    name: 'พันแขนชิงหลัก',
    glyph: '纏',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.4,
    range: 2,
    power: 1.56,
    stun: 2.4,
    fx: 'grab',
    desc: 'พันแขนเกาะติดแขนศัตรูแล้วดึงการ์ดให้หลุด มึนงงนานที่สุด',
  },
  {
    id: 'tf-whip',
    name: 'แขนหลิวหวด',
    glyph: '柳',
    kind: 'attack',
    qi: 20,
    cooldown: 7,
    windup: 0,
    range: 0,
    power: 1.83,
    aoe: 2,
    fx: 'taiji',
    desc: 'กางแขนสะบัดพลังออกรอบตัวดุจแส้ โดนศัตรูทุกตัวในรัศมี 2 ช่อง',
  },
  {
    id: 'tf-clouds',
    name: 'วงมือโอบหมอก',
    glyph: '抱',
    kind: 'heal',
    qi: 0,
    cooldown: 20,
    windup: 0,
    range: 0,
    power: 0,
    healPct: 0.2,
    fx: 'taiji',
    desc: 'หมุนมือดุจเมฆเคลื่อน เดินลมปราณฟื้นพลังชีวิต 20% ในสามวินาที',
  },
  // ── สำนักยอดหมอก: กระบี่วงธารา ──
  {
    id: 'ts-circle',
    name: 'วงกระบี่โอบธาร',
    glyph: '圓',
    kind: 'attack',
    qi: 12,
    cooldown: 3,
    windup: 0,
    range: 2,
    power: 1.07,
    fx: 'taiji',
    desc: 'วาดกระบี่เป็นวงกลมไม่ขาดสาย หนักแน่นแต่ไม่ฝืนแรง',
  },
  {
    id: 'ts-yinyang',
    name: 'ธารแยกผ่าปราการ',
    glyph: '分',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.55,
    range: 2,
    power: 0.98,
    stun: 2,
    fx: 'pierce',
    desc: 'รวมหยินหยางเป็นหนึ่งแล้วผ่าลง ทำลายการตั้งรับด้วยพลังสูงสุดของสำนัก',
  },
  {
    id: 'ts-stars',
    name: 'ดาวพร่างปลายกระบี่',
    glyph: '星',
    kind: 'attack',
    qi: 30,
    cooldown: 11,
    windup: 0,
    range: 5.5,
    ground: true,
    land: 0.55,
    power: 0.47,
    aoe: 2.2,
    fx: 'nine',
    desc: 'ปล่อยปราณกระบี่เจ็ดสายตามตำแหน่งดาวเหนือ พุ่งใส่ศัตรูในระยะ 5 ช่อง',
  },
  {
    id: 'ts-still',
    name: 'นิ่งสยบเคลื่อน',
    glyph: '靜',
    kind: 'heal',
    qi: 0,
    cooldown: 24,
    windup: 0,
    range: 0,
    power: 0,
    healPct: 0.2,
    fx: 'calm',
    desc: 'หยุดนิ่งให้ใจสงบ ใช้ความนิ่งสยบความเคลื่อนไหว ฟื้นพลังชีวิต 20% ในสามวินาที',
  },
  // ── กระบี่ธารเยือกแข็ง ──
  {
    id: 'ss-shift',
    name: 'กิ่งหนาวดีดคืน',
    glyph: '彈',
    kind: 'attack',
    qi: 30,
    cooldown: 2,
    windup: 0,
    range: 2.4,
    power: 1.16,
    applies: { status: 'snow-daze' },
    empoweredBy: { status: 'snow-pride', bonus: 0.6 },
    fx: 'snow',
    desc: 'ร่างนิ่งเกาะตัวพลังกระบี่ไม่ติดขัด ทิ้ง "เหน็บหนาว" ไว้ ทบซ้อน 3 ชั้นศัตรูจะมึน 1 วินาที',
  },
  {
    id: 'ss-frost',
    name: 'ปลายกระบี่เยือกธาร',
    glyph: '寒',
    kind: 'attack',
    qi: 30,
    cooldown: 6,
    windup: 0,
    range: 6,
    power: 1.21,
    dash: 'target',
    self: { status: 'frost-veil' },
    applies: { status: 'slow' },
    fx: 'snowdash',
    desc: 'บุกทะลวงเข้าหาเป้าหมายรวดเร็วไร้โจมตี ตนเองเร็วขึ้น เป้าหมายเคลื่อนที่ช้าลง',
  },
  {
    id: 'ss-gale',
    name: 'หิมะถล่มหน้าผา',
    glyph: '雪',
    kind: 'attack',
    qi: 30,
    cooldown: 8,
    windup: 0,
    range: 6,
    ground: true,
    land: 0.55,
    power: 0.99,
    aoe: 2.8,
    applies: { status: 'chill' },
    empoweredBy: { status: 'snow-pride', bonus: 0.6 },
    fx: 'blizzard',
    desc: 'เลือกพื้นที่ในระยะ 6 ช่อง พายุหิมะโหมลงตรงนั้น ลมหนาวเข้าแทรก ศัตรูในวงช้าลงและใช้วิชาตัวเบาไม่ได้',
  },
  {
    id: 'ss-walk',
    name: 'ไถลบนธารแข็ง',
    glyph: '滑',
    kind: 'buff',
    qi: 0,
    cooldown: 15,
    windup: 0,
    range: 0,
    power: 0,
    cleanse: true,
    self: { status: 'frost-guard' },
    fx: 'snowstep',
    desc: 'ตั้งรับรอ 8 วินาที ถ้าตั้งรับท่าได้จะได้ม่านน้ำแข็งห่อกาย',
  },
  {
    id: 'ss-ice',
    name: 'ธารแข็งแตกร้าว',
    glyph: '凍',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.4,
    range: 2.2,
    power: 1.2,
    stun: 1.2,
    onGuardBreak: { status: 'frost-trap' },
    fx: 'icebreak',
    desc: 'ท่าทำลาย ทลายการตั้งรับ เมื่อการ์ดแตกเป้าหมายติด "ผนึกน้ำแข็ง" ใช้วิชาตัวเบาจะมึน 1 วินาที',
  },
  {
    id: 'ss-pride',
    name: 'ยืนนิ่งกลางลมหนาว',
    glyph: '立',
    kind: 'buff',
    qi: 0,
    cooldown: 1,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'snow-pride' },
    fx: 'snowpride',
    desc: 'สะสม "ใจเหมันต์" สูงสุด 3 ชั้น กิ่งหนาวดีดคืน หิมะถล่มหน้าผา และปุยหิมะร่วงเงียบจะร่ายเป็นร่างเต็มกำลังที่แรงขึ้น',
  },
  {
    id: 'ss-trace',
    name: 'ปุยหิมะร่วงเงียบ',
    glyph: '絮',
    kind: 'attack',
    qi: 30,
    cooldown: 10,
    windup: 0,
    range: 5,
    power: 1.1,
    dash: 'forward',
    empoweredBy: { status: 'snow-pride', bonus: 0.6 },
    fx: 'snowdash',
    desc: 'รวดเร็วดุจลม บุกตรงไปข้างหน้า สร้างความเสียหายให้ศัตรูทุกตัวที่ขวางทาง',
  },
  {
    id: 'ss-hatred',
    name: 'เหมันต์สะกดธาร',
    glyph: '冬',
    kind: 'power',
    qi: 0,
    rage: 10,
    cooldown: 10,
    windup: 0,
    range: 7,
    aoe: 7,
    blink: 0.4,
    power: 0.6,
    hits: 8,
    hitStun: 1,
    perTargetStack: { status: 'snow-daze', bonus: 0.4 },
    fx: 'snowblink',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 วาปหายไปปรากฏข้างศัตรูแล้วฟัน สูงสุด 8 ครั้ง ไล่ฟันทุกตัวในระยะ 7 ช่องทีละตัว ถ้าไม่มีเป้าหมายอื่นจะวนฟันตัวเดิมซ้ำ ฟันแรกของแต่ละตัวทำให้แข็งทื่อ 1 วินาที ยิ่งติดเหน็บหนาวมากยิ่งแรง ระหว่างวาปไม่มีใครแตะต้องได้ ตั้งรับไม่ได้',
  },
  // ── หินระฆัง: หมัดระฆังก้อง ──
  {
    id: 'ar-strike',
    name: 'หมัดวัชระ',
    glyph: '拳',
    kind: 'attack',
    qi: 12,
    cooldown: 2,
    windup: 0,
    range: 2,
    power: 1.38,
    fx: 'vajra',
    desc: 'หมัดหนักดุจวัชระ ตรงไปข้างหน้าไม่มีลูกเล่น',
  },
  {
    id: 'ar-palm',
    name: 'ฝ่ามือพันพุทธ',
    glyph: '掌',
    kind: 'attack',
    qi: 20,
    cooldown: 5,
    windup: 0,
    range: 5,
    ground: true,
    land: 0.55,
    power: 1.5,
    aoe: 2.2,
    fx: 'vajra',
    desc: 'ส่งพลังฝ่ามือเป็นคลื่นทองไปไกล',
  },
  {
    id: 'ar-bell',
    name: 'ระฆังทองคุ้มกาย',
    glyph: '鐘',
    kind: 'buff',
    qi: 0,
    cooldown: 14,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'arhat-guard' },
    fx: 'bell',
    desc: 'ตั้งรับรอ 8 วินาที ถ้าตั้งรับท่าได้จะได้ระฆังอรหันต์ รับความเสียหายลดลง 40% โจมตีแรงขึ้น 10% และได้ความโกรธ',
  },
  {
    id: 'ar-sweep',
    name: 'เตะกวาดพสุธา',
    glyph: '掃',
    kind: 'attack',
    qi: 25,
    cooldown: 8,
    windup: 0,
    range: 0,
    power: 1.12,
    aoe: 2.4,
    applies: { status: 'slow' },
    fx: 'quake',
    desc: 'เตะกวาดพื้นรอบตัว ศัตรูโดยรอบเคลื่อนที่ช้า',
  },
  {
    id: 'ar-break',
    name: 'หมัดทลายภูผา',
    glyph: '崩',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.5,
    range: 2,
    power: 1.75,
    stun: 1.6,
    fx: 'quake',
    desc: 'ท่าทำลาย ทุบการตั้งรับให้แหลกดุจภูผาถล่ม',
  },
  {
    id: 'ar-lion',
    name: 'คำรามราชสีห์',
    glyph: '獅',
    kind: 'attack',
    qi: 30,
    cooldown: 12,
    windup: 0,
    range: 0,
    power: 0.75,
    aoe: 3,
    hitStun: 0.8,
    fx: 'bell',
    desc: 'เสียงคำรามสะท้านใจ ศัตรูโดยรอบมึน 0.8 วินาที',
  },
  {
    id: 'ar-step',
    name: 'ก้าวพระอรหันต์',
    glyph: '步',
    kind: 'attack',
    qi: 20,
    cooldown: 8,
    windup: 0,
    range: 5,
    power: 1.25,
    dash: 'target',
    fx: 'vajra',
    desc: 'ก้าวเดียวถึงตัวศัตรูแล้วกระแทกหมัด',
  },
  {
    id: 'ar-buddha',
    name: 'ฝ่ามือศิลาพันบันได',
    glyph: '佛',
    kind: 'power',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 2.6,
    power: 3,
    rage: 10,
    hitStun: 1.2,
    fx: 'vajra',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 ฝ่ามือยักษ์ฟาดลง ศัตรูมึน 1.2 วินาที ตั้งรับไม่ได้',
  },
  // ── ทางโคมเหล็ก: ดาบขอเกี่ยวโคม ──
  {
    id: 'jy-cut',
    name: 'ฟันเฉือนเลือด',
    glyph: '斬',
    kind: 'attack',
    qi: 12,
    cooldown: 2,
    windup: 0,
    range: 2,
    power: 1.05,
    applies: { status: 'bleed' },
    fx: 'sabre',
    desc: 'ดาบเฉือนเป็นแผล ศัตรูโลหิตไหล',
  },
  {
    id: 'jy-cross',
    name: 'ดาบกากบาท',
    glyph: '叉',
    kind: 'attack',
    qi: 20,
    cooldown: 5,
    windup: 0,
    range: 2,
    power: 0.7,
    hits: 2,
    applies: { status: 'bleed' },
    fx: 'chain',
    desc: 'ฟันไขว้สองครั้งติดกัน แต่ละครั้งทำให้โลหิตไหล',
  },
  {
    id: 'jy-whirl',
    name: 'พายุดาบหมุน',
    glyph: '旋',
    kind: 'attack',
    qi: 28,
    cooldown: 9,
    windup: 0,
    range: 5,
    ground: true,
    land: 0.45,
    power: 1,
    aoe: 2.4,
    applies: { status: 'bleed' },
    fx: 'whirl',
    desc: 'หมุนตัวฟันรอบทิศ ทุกคนโดยรอบโลหิตไหล',
  },
  {
    id: 'jy-lunge',
    name: 'พุ่งทะลวงราชวัง',
    glyph: '衝',
    kind: 'attack',
    qi: 22,
    cooldown: 7,
    windup: 0,
    range: 5,
    power: 1.15,
    dash: 'target',
    fx: 'sabre',
    desc: 'พุ่งเข้าหาเป้าหมายแล้วฟันทันที',
  },
  {
    id: 'jy-shatter',
    name: 'ดาบทลายเกราะ',
    glyph: '碎',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.45,
    range: 2,
    power: 1.4,
    stun: 1.5,
    fx: 'sabre',
    desc: 'ท่าทำลาย ฟันทลายการตั้งรับ',
  },
  {
    id: 'jy-oath',
    name: 'สาบานต่อบัลลังก์',
    glyph: '誓',
    kind: 'buff',
    qi: 0,
    cooldown: 16,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'brocade-guard' },
    fx: 'aura',
    desc: 'ตั้งรับรอ 8 วินาที ถ้าตั้งรับท่าได้จะได้สัตย์ดาบแพรทอง โจมตีแรงขึ้น 20% คริติคอลสูงขึ้น 10%',
  },
  {
    id: 'jy-execute',
    name: 'ประหารไร้เมตตา',
    glyph: '誅',
    kind: 'attack',
    qi: 30,
    cooldown: 10,
    windup: 0,
    range: 2,
    power: 0.8,
    perTargetStack: { status: 'bleed', bonus: 0.5 },
    fx: 'chain',
    desc: 'ยิ่งศัตรูโลหิตไหลมากยิ่งแรง ใช้ชั้นโลหิตไหลทั้งหมด',
  },
  {
    id: 'jy-dragon',
    name: 'ดาบมังกรผ่าฟ้า',
    glyph: '龍',
    kind: 'power',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 3,
    power: 0.8,
    rage: 10,
    hits: 3,
    hitStun: 0.8,
    fx: 'chain',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 ฟันสามครั้งดุจมังกร ศัตรูมึน 0.8 วินาที ตั้งรับไม่ได้',
  },
  // ── จันทร์มัว: ฝ่ามือเงียบจันทร์มัว ──
  {
    id: 'jl-claw',
    name: 'กรงเล็บมาร',
    glyph: '爪',
    kind: 'attack',
    qi: 12,
    cooldown: 2,
    windup: 0,
    range: 2,
    power: 1.05,
    applies: { status: 'poison' },
    fx: 'shadow',
    desc: 'กรงเล็บอาบพิษ ทิ้งพิษจันทร์มัวไว้',
  },
  {
    id: 'jl-needle',
    name: 'เข็มเงาพิษ',
    glyph: '針',
    kind: 'attack',
    qi: 20,
    cooldown: 6,
    windup: 0,
    range: 6,
    power: 0.5,
    volley: 4,
    applies: { status: 'poison' },
    fx: 'needles',
    desc: 'ซัดเข็มพิษสี่เล่มใส่ศัตรูในระยะ',
  },
  {
    id: 'jl-charm',
    name: 'ร่ายมนตร์จันทร์มัว',
    glyph: '媚',
    kind: 'attack',
    qi: 20,
    cooldown: 10,
    windup: 0,
    range: 0,
    power: 0.4,
    aoe: 2.6,
    applies: { status: 'charm' },
    fx: 'shadow',
    desc: 'มนตร์สะกดรอบตัว ศัตรูโจมตีเบาลงและช้าลง',
  },
  {
    id: 'jl-step',
    name: 'ก้าวเงาลวงตา',
    glyph: '幻',
    kind: 'buff',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 0,
    power: 0,
    cleanse: true,
    self: { status: 'bliss-guard' },
    fx: 'aura',
    desc: 'ตั้งรับรอ 8 วินาที ถ้าตั้งรับท่าได้จะได้ก้าวสราญ เคลื่อนที่เร็วขึ้น 20% ดูดเลือด 5%',
  },
  {
    id: 'jl-soul',
    name: 'ฝ่ามือดูดวิญญาณ',
    glyph: '魂',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.45,
    range: 2,
    power: 1.3,
    stun: 1.4,
    onGuardBreak: { status: 'charm' },
    fx: 'shadow',
    desc: 'ท่าทำลาย ทลายการตั้งรับ เมื่อการ์ดแตกศัตรูติดมนตร์จันทร์มัว',
  },
  {
    id: 'jl-drain',
    name: 'ดื่มโลหิต',
    glyph: '噬',
    kind: 'attack',
    qi: 25,
    cooldown: 10,
    windup: 0,
    range: 2,
    power: 1,
    lifesteal: 0.5,
    fx: 'shadow',
    desc: 'ดูดเลือดของศัตรูกลับมาเป็นของตน',
  },
  {
    id: 'jl-bloom',
    name: 'ดอกท้อพิษสะพรั่ง',
    glyph: '桃',
    kind: 'attack',
    qi: 30,
    cooldown: 12,
    windup: 0,
    range: 6,
    ground: true,
    land: 0.5,
    power: 0.7,
    aoe: 2.6,
    applies: { status: 'poison', stacks: 2 },
    fx: 'shadow',
    desc: 'ละอองพิษบานสะพรั่งรอบตัว ติดพิษสองชั้น',
  },
  {
    id: 'jl-abyss',
    name: 'ฝ่ามือมารนรก',
    glyph: '獄',
    kind: 'power',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 2,
    power: 1.2,
    rage: 10,
    hitStun: 1,
    perTargetStack: { status: 'poison', bonus: 0.4 },
    fx: 'shadow',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 ยิ่งศัตรูติดพิษมากยิ่งแรง ศัตรูมึน 1 วินาที ตั้งรับไม่ได้',
  },
  // ── หอหมึกสน: กระบี่แท่งหมึกสน ──
  {
    id: 'jz-stroke',
    name: 'ลายพู่กันตวัด',
    glyph: '筆',
    kind: 'attack',
    qi: 12,
    cooldown: 2,
    windup: 0,
    range: 2.2,
    power: 1.05,
    fx: 'brush',
    desc: 'กระบี่ตวัดดุจลายพู่กัน เบาแต่คม',
  },
  {
    id: 'jz-poem',
    name: 'กลอนกระบี่ห้าคำ',
    glyph: '詩',
    kind: 'attack',
    qi: 22,
    cooldown: 6,
    windup: 0,
    range: 2.2,
    power: 0.6,
    hits: 3,
    fx: 'brush',
    desc: 'แทงสามครั้งเป็นจังหวะดุจกลอน',
  },
  {
    id: 'jz-ink',
    name: 'หมึกสาดทั่วทิศ',
    glyph: '墨',
    kind: 'attack',
    qi: 28,
    cooldown: 9,
    windup: 0,
    range: 5,
    ground: true,
    land: 0.5,
    power: 0.9,
    aoe: 2.4,
    applies: { status: 'slow' },
    fx: 'ink',
    desc: 'สาดพลังหมึกรอบตัว ศัตรูเคลื่อนที่ช้า',
  },
  {
    id: 'jz-seal',
    name: 'จิ้มจุดปิดชีพจร',
    glyph: '點',
    kind: 'attack',
    qi: 20,
    cooldown: 12,
    windup: 0,
    range: 2,
    power: 0.6,
    applies: { status: 'silence' },
    fx: 'seal',
    desc: 'จิ้มจุดชีพจร ศัตรูใช้กระบวนท่าไม่ได้ 2 วินาที',
  },
  {
    id: 'jz-ward',
    name: 'พัดลมบังกาย',
    glyph: '扇',
    kind: 'buff',
    qi: 0,
    cooldown: 14,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'brush-guard' },
    fx: 'aura',
    desc: 'ตั้งรับรอ 8 วินาที ถ้าตั้งรับท่าได้จะได้ปราณหมึกกำบัง รับความเสียหายลดลง 20% คริติคอลสูงขึ้น 15%',
  },
  {
    id: 'jz-focus',
    name: 'สมาธิหมึกนิ่ง',
    glyph: '悟',
    kind: 'buff',
    qi: 0,
    cooldown: 16,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'focus' },
    fx: 'aura',
    desc: 'จิตนิ่งแน่วแน่ โอกาสคริติคอล +25% 6 วินาที',
  },
  {
    id: 'jz-break',
    name: 'ตัวอักษรทลายค่าย',
    glyph: '字',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.45,
    range: 2.2,
    power: 1.35,
    stun: 1.4,
    fx: 'brush',
    desc: 'ท่าทำลาย เขียนตัวอักษรกลางอากาศทลายการตั้งรับ',
  },
  {
    id: 'jz-scroll',
    name: 'ม้วนคัมภีร์สะท้านฟ้า',
    glyph: '卷',
    kind: 'power',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 0,
    power: 1.5,
    rage: 10,
    aoe: 3,
    hitStun: 0.8,
    fx: 'ink',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 คลี่คัมภีร์ปลดพลังรอบตัว ศัตรูมึน 0.8 วินาที ตั้งรับไม่ได้',
  },
  // ── ดาบชาด: ดาบชาดคลั่งไฟ ──
  {
    id: 'bs-slash',
    name: 'ดาบชาดฟาด',
    glyph: '刃',
    kind: 'attack',
    qi: 12,
    cooldown: 2,
    windup: 0,
    range: 2.2,
    power: 1.1,
    applies: { status: 'bleed' },
    fx: 'blood',
    desc: 'ดาบใหญ่ฟาดลงหนักหน่วง ศัตรูโลหิตไหล',
  },
  {
    id: 'bs-rend',
    name: 'ฉีกเนื้อเลือดสาด',
    glyph: '裂',
    kind: 'attack',
    qi: 20,
    cooldown: 5,
    windup: 0,
    range: 2.2,
    power: 0.75,
    hits: 2,
    applies: { status: 'bleed' },
    fx: 'blood',
    desc: 'ฟันสองครั้งติดกัน ฉีกเนื้อจนเลือดสาด แต่ละครั้งทำให้โลหิตไหล',
  },
  {
    id: 'bs-whirl',
    name: 'พายุโลหิต',
    glyph: '紅',
    kind: 'attack',
    qi: 28,
    cooldown: 9,
    windup: 0,
    range: 5,
    ground: true,
    land: 0.5,
    aoe: 2.4,
    power: 1,
    applies: { status: 'bleed' },
    fx: 'blood',
    desc: 'หมุนดาบเป็นพายุเลือดรอบตัว ทุกคนโดยรอบโลหิตไหล',
  },
  {
    id: 'bs-charge',
    name: 'บุกสังหาร',
    glyph: '屠',
    kind: 'attack',
    qi: 22,
    cooldown: 7,
    windup: 0,
    range: 6,
    power: 1.2,
    dash: 'target',
    fx: 'blood',
    desc: 'พุ่งเข้าหาศัตรูแล้วฟันฉับ ระยะไกล',
  },
  {
    id: 'bs-break',
    name: 'ทลายกระดูก',
    glyph: '骨',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.45,
    range: 2.2,
    power: 1.45,
    stun: 1.5,
    onGuardBreak: { status: 'bleed' },
    fx: 'blood',
    desc: 'ท่าทำลาย ทุ่มดาบทลายการตั้งรับ กระดูกศัตรูแหลกละเอียด',
  },
  {
    id: 'bs-frenzy',
    name: 'คลั่งโลหิต',
    glyph: '狂',
    kind: 'buff',
    qi: 0,
    cooldown: 16,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'sabre-guard' },
    fx: 'blood',
    desc: 'ตั้งรับรอ 8 วินาที ถ้าตั้งรับท่าได้จะได้โลหิตดาบคลั่ง โจมตีแรงขึ้น 25% ดูดเลือด 8% และได้ความโกรธ',
  },
  {
    id: 'bs-drink',
    name: 'ดื่มไฟศัตรู',
    glyph: '飲',
    kind: 'attack',
    qi: 25,
    cooldown: 10,
    windup: 0,
    range: 2.2,
    power: 0.9,
    lifesteal: 0.5,
    perTargetStack: { status: 'bleed', bonus: 0.4 },
    fx: 'blood',
    desc: 'ฟันแล้วดูดเลือดกลับมาครึ่งหนึ่ง ยิ่งศัตรูโลหิตไหลมากยิ่งแรง',
  },
  {
    id: 'bs-massacre',
    name: 'โลหิตคลั่งกระหายเลือด',
    glyph: '戮',
    kind: 'power',
    qi: 0,
    cooldown: 14,
    windup: 0,
    range: 0,
    power: 0.45,
    rage: 10,
    aoe: 2.8,
    spin: { duration: 6, tick: 0.4 },
    lifesteal: 0.25,
    fx: 'bloodstorm',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 คลั่งกระหายเลือด เหวี่ยงดาบหมุนวนรอบตัว 6 วินาที ฟันทุกคนในระยะ 2.8 ช่องต่อเนื่อง ดูดเลือดทุกครั้งที่ฟันโดน เดินไปพร้อมกันได้ ตั้งรับไม่ได้',
  },
  // ── ท่าเพิ่มให้วิชาชุดแรกครบ 8 ท่า ──
  {
    id: 'ns-sweep',
    name: 'กระบี่กวาดทัพ',
    glyph: '掃',
    kind: 'attack',
    qi: 22,
    cooldown: 8,
    windup: 0,
    range: 5,
    ground: true,
    land: 0.5,
    power: 1,
    aoe: 2.6,
    fx: 'whirl',
    desc: 'กวาดกระบี่รอบตัว ฟันศัตรูทุกตัวโดยรอบ',
  },
  {
    id: 'ns-rush',
    name: 'ร่องศิลาทะลวงฟ้า',
    glyph: '衝',
    kind: 'attack',
    qi: 20,
    cooldown: 7,
    windup: 0,
    range: 5,
    power: 1.15,
    dash: 'forward',
    fx: 'pierce',
    desc: 'พุ่งทะลวงไปข้างหน้า ฟันทุกตัวที่ขวางทาง',
  },
  {
    id: 'ns-oath',
    name: 'สัญญาเลือดปลุกกระบี่',
    glyph: '誓',
    kind: 'buff',
    qi: 0,
    cooldown: 16,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'nine-guard' },
    fx: 'aura',
    desc: 'ตั้งรับรอ 8 วินาที ถ้าตั้งรับท่าได้จะได้ปณิธานเก้ากระบี่ โจมตีแรงขึ้น 15% คริติคอลสูงขึ้น 10% และได้ความโกรธ',
  },
  {
    id: 'ns-heaven',
    name: 'ศิลาตื่นสะท้านฟ้า',
    glyph: '斬',
    kind: 'power',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 6,
    power: 0.8,
    rage: 10,
    ground: true,
    land: 0.6,
    aoe: 3,
    hits: 3,
    hitStun: 1,
    fx: 'bolt',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 เลือกพื้นที่ในระยะ 6 ช่อง อัสนีเก้ากระบี่ฟาดลงสามครั้ง ศัตรูในวงมึน 1 วินาที ตั้งรับไม่ได้',
  },
  {
    id: 'cp-drift',
    name: 'เมฆาลอยหลบ',
    glyph: '霞',
    kind: 'buff',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 0,
    power: 0,
    cleanse: true,
    self: { status: 'cloud-guard' },
    fx: 'aura',
    desc: 'ตั้งรับรอ 8 วินาที ถ้าตั้งรับท่าได้จะได้ก้าวเบาดุจเมฆ เคลื่อนที่เร็วขึ้น 35% และได้ลมปราณคืน',
  },
  {
    id: 'cp-roar',
    name: 'ฝ่ามือเมฆาคำราม',
    glyph: '吼',
    kind: 'attack',
    qi: 20,
    cooldown: 6,
    windup: 0,
    range: 4,
    power: 1.62,
    applies: { status: 'slow' },
    fx: 'palm',
    desc: 'ส่งฝ่ามือไกล ศัตรูเคลื่อนที่ช้าลง',
  },
  {
    id: 'cp-ring',
    name: 'วงเมฆาล้อม',
    glyph: '環',
    kind: 'attack',
    qi: 26,
    cooldown: 10,
    windup: 0,
    range: 6,
    power: 1.25,
    ground: true,
    land: 0.5,
    aoe: 2.5,
    applies: { status: 'chill' },
    fx: 'whirl',
    desc: 'เลือกพื้นที่ในระยะ 6 ช่อง เมฆหนาวห่อหุ้ม ศัตรูในวงช้าลงและใช้วิชาตัวเบาไม่ได้',
  },
  {
    id: 'cp-heaven',
    name: 'ฝ่ามือเมฆาครอบฟ้า',
    glyph: '穹',
    kind: 'power',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 0,
    power: 2.36,
    rage: 10,
    aoe: 3.2,
    hitStun: 1,
    fx: 'palm',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 ฝ่ามือเมฆาครอบรอบตัว ศัตรูโดยรอบมึน 1 วินาที ตั้งรับไม่ได้',
  },
  {
    id: 'bs-glide',
    name: 'เงาไผ่พาดผ่าน',
    glyph: '颯',
    kind: 'attack',
    qi: 20,
    cooldown: 7,
    windup: 0,
    range: 5,
    power: 1.06,
    dash: 'forward',
    fx: 'pierce',
    desc: 'พุ่งผ่านศัตรูดุจสายลม ฟันทุกตัวที่ขวางทาง',
  },
  {
    id: 'bs-mist',
    name: 'หมอกลมคลุมกาย',
    glyph: '霧',
    kind: 'buff',
    qi: 0,
    cooldown: 14,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'breeze-guard' },
    fx: 'aura',
    desc: 'ตั้งรับรอ 8 วินาที ถ้าตั้งรับท่าได้จะได้ม่านหมอกลมห่อกาย รับความเสียหายลดลง 20% และเคลื่อนที่เร็วขึ้น 15%',
  },
  {
    id: 'bs-reed',
    name: 'ต้นอ้อลู่ลม',
    glyph: '蘆',
    kind: 'attack',
    qi: 22,
    cooldown: 6,
    windup: 0,
    range: 2.2,
    power: 0.53,
    hits: 3,
    fx: 'chain',
    desc: 'ฟันสามครั้งพลิ้วดุจต้นอ้อ',
  },
  {
    id: 'bs-tempest',
    name: 'พายุป่าไผ่',
    glyph: '颶',
    kind: 'power',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 0,
    power: 0.86,
    rage: 10,
    aoe: 3,
    hits: 2,
    hitStun: 0.8,
    fx: 'whirl',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 หมุนกระบี่เป็นพายุสองรอบ ศัตรูโดยรอบมึน 0.8 วินาที ตั้งรับไม่ได้',
  },
  {
    id: 'tf-yield',
    name: 'หมอกอ่อนรับภูผา',
    glyph: '柔',
    kind: 'buff',
    qi: 0,
    cooldown: 14,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'yield-guard' },
    fx: 'taiji',
    desc: 'ตั้งรับรอ 8 วินาที ถ้าตั้งรับท่าได้จะได้อ่อนหยุ่นรับแรง รับความเสียหายลดลง 40% และฟื้นพลังชีวิตเล็กน้อย',
  },
  {
    id: 'tf-step',
    name: 'ก้าววนกลางหมอก',
    glyph: '步',
    kind: 'attack',
    qi: 20,
    cooldown: 7,
    windup: 0,
    range: 5,
    power: 1.37,
    dash: 'target',
    fx: 'palm',
    desc: 'ก้าวเดียวถึงตัวศัตรูแล้วผลักฝ่ามือ',
  },
  {
    id: 'tf-wheel',
    name: 'วงล้อหยินหยาง',
    glyph: '輪',
    kind: 'attack',
    qi: 26,
    cooldown: 10,
    windup: 0,
    range: 6,
    power: 1.18,
    ground: true,
    land: 0.5,
    aoe: 2.6,
    fx: 'taiji',
    desc: 'เลือกพื้นที่ในระยะ 6 ช่อง วงหยินหยางหมุนบดศัตรูในวง',
  },
  {
    id: 'tf-supreme',
    name: 'หมอกวนครอบฟ้า',
    glyph: '迴',
    kind: 'power',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 0,
    power: 2.21,
    rage: 10,
    aoe: 3,
    hitStun: 1.2,
    fx: 'taiji',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 หมุนวงหมอกใหญ่รอบตัว ศัตรูโดยรอบมึน 1.2 วินาที ตั้งรับไม่ได้',
  },
  {
    id: 'ts-flow',
    name: 'กระบี่ไหลตามน้ำ',
    glyph: '流',
    kind: 'attack',
    qi: 20,
    cooldown: 5,
    windup: 0,
    range: 2.2,
    power: 0.44,
    hits: 2,
    fx: 'chain',
    desc: 'ฟันสองครั้งต่อเนื่องดุจสายน้ำ',
  },
  {
    id: 'ts-thread',
    name: 'เส้นด้ายกระบี่',
    glyph: '線',
    kind: 'attack',
    qi: 22,
    cooldown: 7,
    windup: 0,
    range: 5,
    power: 0.7,
    dash: 'forward',
    fx: 'pierce',
    desc: 'พุ่งเป็นเส้นตรงดุจด้าย ฟันทุกตัวที่ขวางทาง',
  },
  {
    id: 'ts-focus',
    name: 'จิตสงบกระบี่นิ่ง',
    glyph: '寧',
    kind: 'buff',
    qi: 0,
    cooldown: 16,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'focus-guard' },
    fx: 'aura',
    desc: 'ตั้งรับรอ 8 วินาที ถ้าตั้งรับท่าได้จะได้สมาธิกระบี่นิ่ง และได้ลมปราณคืน',
  },
  {
    id: 'ts-heaven',
    name: 'วงธาราผนึกฟ้า',
    glyph: '封',
    kind: 'power',
    qi: 0,
    cooldown: 12,
    windup: 0,
    range: 6,
    power: 0.51,
    rage: 10,
    ground: true,
    land: 0.6,
    aoe: 3,
    hits: 3,
    hitStun: 1,
    fx: 'taiji',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 เลือกพื้นที่ในระยะ 6 ช่อง วงกระบี่วงธาราฟันสามครั้ง ศัตรูในวงมึน 1 วินาที ตั้งรับไม่ได้',
  },
  // ── ศัตรู ──
  {
    id: 'wolf-bite',
    name: 'งับ',
    glyph: '咬',
    kind: 'attack',
    qi: 0,
    cooldown: 0,
    windup: 0.35,
    range: 1.7,
    power: 1,
    fx: 'bite',
    desc: '',
  },
  {
    id: 'bandit-slash',
    name: 'ฟันดาบ',
    glyph: '斬',
    kind: 'attack',
    qi: 0,
    cooldown: 0,
    windup: 0.45,
    range: 2.0,
    power: 1,
    fx: 'slash',
    desc: '',
  },
  {
    id: 'bandit-crush',
    name: 'ทุบเกราะ',
    glyph: '破',
    kind: 'break',
    qi: 0,
    cooldown: 5,
    windup: 0.8,
    range: 2.1,
    power: 1.3,
    stun: 1.5,
    fx: 'crush',
    desc: '',
  },
  {
    id: 'assassin-stab',
    name: 'แทงเงา',
    glyph: '刺',
    kind: 'attack',
    qi: 0,
    cooldown: 0,
    windup: 0.3,
    range: 1.8,
    power: 0.95,
    fx: 'pierce',
    desc: '',
  },
  {
    id: 'assassin-feint',
    name: 'ลวงเงา',
    glyph: '影',
    kind: 'break',
    qi: 0,
    cooldown: 3,
    windup: 0.5,
    range: 1.9,
    power: 1.2,
    stun: 1.2,
    fx: 'pierce',
    desc: '',
  },
  {
    id: 'monk-staff',
    name: 'กระบองนอกรีต',
    glyph: '棍',
    kind: 'attack',
    qi: 0,
    cooldown: 0,
    windup: 0.5,
    range: 2.2,
    power: 1.1,
    fx: 'crush',
    desc: '',
  },
  {
    id: 'monk-sweep',
    name: 'กวาดพสุธา',
    glyph: '掃',
    kind: 'break',
    qi: 0,
    cooldown: 6,
    windup: 0.9,
    range: 2.3,
    power: 1.4,
    stun: 1.6,
    fx: 'quake',
    desc: '',
  },
  {
    id: 'bear-swipe',
    name: 'ตะปบหมี',
    glyph: '爪',
    kind: 'attack',
    qi: 0,
    cooldown: 0,
    windup: 0.55,
    range: 1.9,
    power: 1.4,
    fx: 'bite',
    desc: '',
  },
  {
    id: 'bear-maul',
    name: 'ขย้ำหมี',
    glyph: '熊',
    kind: 'break',
    qi: 0,
    cooldown: 6,
    windup: 1.0,
    range: 2.0,
    power: 1.8,
    stun: 1.8,
    fx: 'crush',
    desc: '',
  },
  {
    id: 'knife-throw',
    name: 'มีดบิน',
    glyph: '刃',
    kind: 'attack',
    qi: 0,
    cooldown: 0,
    windup: 0.55,
    range: 6,
    power: 0.45,
    volley: 2,
    fx: 'needles',
    desc: '',
  },
  {
    id: 'jiangshi-claw',
    name: 'เล็บผีดิบ',
    glyph: '屍',
    kind: 'attack',
    qi: 0,
    cooldown: 0,
    windup: 0.5,
    range: 1.7,
    power: 1,
    applies: { status: 'poison' },
    fx: 'bite',
    desc: '',
  },
  {
    id: 'boar-gore',
    name: 'ขวิดเขี้ยว',
    glyph: '牙',
    kind: 'attack',
    qi: 0,
    cooldown: 0,
    windup: 0.45,
    range: 1.8,
    power: 1.1,
    fx: 'crush',
    desc: '',
  },
  {
    id: 'boar-charge',
    name: 'พุ่งชนเขี้ยวเหล็ก',
    glyph: '衝',
    kind: 'break',
    qi: 0,
    cooldown: 6,
    windup: 0.85,
    range: 1.9,
    power: 1.4,
    stun: 1.4,
    fx: 'crush',
    desc: '',
  },
  {
    id: 'viper-fang',
    name: 'เขี้ยวพิษหยก',
    glyph: '蛇',
    kind: 'attack',
    qi: 0,
    cooldown: 0,
    windup: 0.3,
    range: 1.7,
    power: 0.85,
    applies: { status: 'poison' },
    fx: 'bite',
    desc: '',
  },
  {
    id: 'scorpion-pincer',
    name: 'ก้ามหนีบ',
    glyph: '鉗',
    kind: 'attack',
    qi: 0,
    cooldown: 0,
    windup: 0.45,
    range: 1.8,
    power: 1,
    fx: 'grab',
    desc: '',
  },
  {
    id: 'scorpion-sting',
    name: 'เหล็กในเพลิง',
    glyph: '蠍',
    kind: 'break',
    qi: 0,
    cooldown: 6,
    windup: 0.85,
    range: 1.9,
    power: 1.4,
    stun: 1.3,
    applies: { status: 'poison', stacks: 2 },
    fx: 'pierce',
    desc: '',
  },
  {
    id: 'leopard-claw',
    name: 'ตะปบเงา',
    glyph: '豹',
    kind: 'attack',
    qi: 0,
    cooldown: 0,
    windup: 0.3,
    range: 1.8,
    power: 1,
    applies: { status: 'bleed' },
    fx: 'slash',
    desc: '',
  },
  {
    id: 'bat-fang',
    name: 'ดูดโลหิต',
    glyph: '蝠',
    kind: 'attack',
    qi: 0,
    cooldown: 0,
    windup: 0.3,
    range: 1.7,
    power: 0.85,
    applies: { status: 'bleed' },
    fx: 'bite',
    desc: '',
  },
  {
    id: 'spider-fang',
    name: 'เขี้ยวแมงมุม',
    glyph: '蛛',
    kind: 'attack',
    qi: 0,
    cooldown: 0,
    windup: 0.45,
    range: 1.8,
    power: 1,
    applies: { status: 'poison' },
    fx: 'bite',
    desc: '',
  },
  {
    id: 'spider-web',
    name: 'พ่นใยพันธนาการ',
    glyph: '網',
    kind: 'break',
    qi: 0,
    cooldown: 6,
    windup: 0.8,
    range: 2,
    power: 1.1,
    stun: 1.2,
    applies: { status: 'slow' },
    fx: 'grab',
    desc: '',
  },
  {
    id: 'centipede-bite',
    name: 'กรามพิษร้อยขา',
    glyph: '蜈',
    kind: 'attack',
    qi: 0,
    cooldown: 0,
    windup: 0.4,
    range: 1.8,
    power: 1.05,
    applies: { status: 'poison', stacks: 2 },
    fx: 'bite',
    desc: '',
  },
  {
    id: 'python-strike',
    name: 'ฉกเหลือม',
    glyph: '蟒',
    kind: 'attack',
    qi: 0,
    cooldown: 0,
    windup: 0.5,
    range: 2,
    power: 1.2,
    fx: 'bite',
    desc: '',
  },
  {
    id: 'python-coil',
    name: 'รัดกระดูก',
    glyph: '纏',
    kind: 'break',
    qi: 0,
    cooldown: 6,
    windup: 1.0,
    range: 2.1,
    power: 1.6,
    stun: 2,
    fx: 'grab',
    desc: '',
  },
  {
    id: 'fox-claw',
    name: 'กรงเล็บจิ้งจอก',
    glyph: '狐',
    kind: 'attack',
    qi: 0,
    cooldown: 0,
    windup: 0.35,
    range: 1.8,
    power: 1,
    fx: 'slash',
    desc: '',
  },
  {
    id: 'fox-charm',
    name: 'มนตร์เก้าหาง',
    glyph: '魅',
    kind: 'break',
    qi: 0,
    cooldown: 6,
    windup: 0.9,
    range: 2,
    power: 1.2,
    stun: 1.2,
    applies: { status: 'charm' },
    fx: 'shadow',
    desc: '',
  },
  {
    id: 'tiger-claw',
    name: 'อุ้งพยัคฆ์',
    glyph: '虎',
    kind: 'attack',
    qi: 0,
    cooldown: 0,
    windup: 0.45,
    range: 2,
    power: 1.3,
    applies: { status: 'bleed' },
    fx: 'slash',
    desc: '',
  },
  {
    id: 'tiger-pounce',
    name: 'พยัคฆ์ตะครุบ',
    glyph: '撲',
    kind: 'break',
    qi: 0,
    cooldown: 6,
    windup: 0.9,
    range: 2.1,
    power: 1.8,
    stun: 1.8,
    fx: 'crush',
    desc: '',
  },
  {
    id: 'boss-slash',
    name: 'กระบี่ทมิฬ',
    glyph: '斬',
    kind: 'attack',
    qi: 0,
    cooldown: 0,
    windup: 0.4,
    range: 2.5,
    power: 1.1,
    fx: 'slash',
    desc: '',
  },
  {
    id: 'boss-cleave',
    name: 'ผ่าปราการ',
    glyph: '破',
    kind: 'break',
    qi: 0,
    cooldown: 6,
    windup: 0.75,
    range: 2.5,
    power: 1.5,
    stun: 2,
    fx: 'cleave',
    desc: '',
  },
  {
    id: 'wb-rain',
    name: 'ฝนพันกระบี่',
    glyph: '劍',
    kind: 'break',
    qi: 0,
    cooldown: 8,
    windup: 0,
    range: 9,
    power: 2.6,
    stun: 1.2,
    telegraph: { radius: 3.2, delay: 1.4 },
    fx: 'ring',
    desc: '',
  },
  {
    id: 'boss-ring',
    name: 'วงปราณโลหิต',
    glyph: '血',
    kind: 'break',
    qi: 0,
    cooldown: 7,
    windup: 0,
    range: 7,
    power: 2.3,
    stun: 1,
    telegraph: { radius: 1.8, delay: 1.15 },
    fx: 'ring',
    desc: '',
  },
  // ── วิชาลำดับที่ 2–3 ของแต่ละสำนัก และวิชายุทธจักรเพิ่ม (data/arts.ts) ──
  ...EXTRA_SKILLS,
  // ── ฝ่ามือมังกรทองเก้าสวรรค์ (data/dragonPalm.ts) ──
  ...DRAGON_PALM_SKILLS,
  // ── ไม้พลองคลื่นไผ่ (data/staffArt.ts, prototype, hidden) ──
  ...STAFF_SKILLS,
  // ── อาวุธลับ: thrown from the hidden-weapon slot (data/hiddenWeapons.ts) ──
  ...HIDDEN_SKILLS,
  // ── ถ้ำคลื่นคำราม: the dungeon's people and its boss (data/dungeon.ts) ──
  ...DUNGEON_SKILLS,
  // ── บอสโลก: their moves (data/worldBosses.ts) ──
  ...WORLD_BOSS_SKILLS,
];

export const SKILLS: Readonly<Record<string, SkillDef>> = Object.fromEntries(
  list.map((s) => {
    // The lock moves (data/lockForms.ts) carry their form on the move itself.
    if (LOCK_FORMS[s.id]) (s as { lockForm?: LockFormDef }).lockForm = LOCK_FORMS[s.id];
    if (LOCK_AIM_FORCED.has(s.id)) (s as { aim?: string }).aim = 'lock';
    return [s.id, s];
  }),
);

export function getSkill(id: string): SkillDef {
  const s = SKILLS[id];
  if (!s) throw new Error(`Unknown skill: ${id}`);
  return s;
}

export type { Weapon };

export interface MartialArtDef {
  readonly id: string;
  /**
   * A legendary art (วิชาในตำนาน): in the data for the lore, NPCs and bosses, but not for players: not in the starting kit,
   * not learnable, not listed in the arts panel, not on offer in any shop. Saves that hold it are moved to `STARTER_ART`.
   */
  readonly hidden?: boolean;
  /**
   * A tournament prize (data/legendary.ts, C0-TOURNEY): held for one week by the champion of ประลองยุทธภพ, never learned or
   * traded. Full power against NPCs, monsters and bosses; normalised against players. Not `hidden`: its holder uses it.
   */
  readonly legendary?: boolean;
  readonly name: string;
  readonly school: string;
  readonly weapon: Weapon;
  readonly desc: string;
  /** Up to eight moves, bound to keys 1–8 while this art is active. */
  readonly skills: readonly string[];
  /** ธาตุ of the art; matching the active neigong's element adds power. */
  readonly element: Element;
  /** External arts use melee power, accuracy and defence; internal arts their inner counterparts. */
  readonly damage: DamageType;
  /** The automatic attack used when engaging a target. Always attack-kind. */
  readonly basic: { readonly power: number; readonly range: number; readonly rate: number };
  /** School that teaches this art; learned through the school panel instead of quests. */
  readonly schoolId?: string;
  /** Cultivation cost to learn from the school (0 = given on joining). */
  readonly learnCost?: number;
  /** Art that must be learned first. */
  readonly requires?: string;
}

const arts: MartialArtDef[] = [
  {
    id: 'nine-swords',
    hidden: true,
    name: 'เพลงกระบี่ร่องศิลา',
    school: 'ศิลาเก้ากระบี่',
    weapon: 'sword',
    element: 'yang',
    damage: 'external',
    desc: 'วิชากระบี่ที่ผู้ลงนามสัญญาเลือดได้รับจากศิลา โจมตีไกลและกวาดศัตรูเป็นกลุ่ม',
    skills: ['ns-thunder', 'ns-pierce', 'ns-nine', 'ns-calm', 'ns-sweep', 'ns-rush', 'ns-oath', 'ns-heaven'],
    basic: { power: 1, range: 1.95, rate: 0.8 },
  },
  {
    id: 'cloud-palm',
    name: 'ฝ่ามือเมฆาคล้อย',
    school: 'หุบเขาเมฆา',
    weapon: 'unarmed',
    element: 'soft',
    damage: 'internal',
    desc: 'วิชาฝ่ามือมือเปล่า จังหวะเร็ว ท่าทำลายชาร์จไว เหมาะกับการปั่นป่วนการตั้งรับ',
    skills: ['cp-wave', 'cp-seize', 'cp-storm', 'cp-breath', 'cp-drift', 'cp-roar', 'cp-ring', 'cp-heaven'],
    basic: { power: 1.19, range: 1.65, rate: 0.6 },
  },
  {
    id: 'breeze-sword',
    name: 'กระบี่ไผ่ห่มหมอก',
    school: 'ยอดหมอก',
    schoolId: 'mist-peak',
    learnCost: 0,
    weapon: 'sword',
    element: 'soft',
    damage: 'external',
    desc: 'วิชากระบี่พื้นฐานของยอดหมอก เบาและเร็วดุจสายลม ใช้กระบวนท่าต่อเนื่องได้บ่อย',
    skills: ['bs-gust', 'bs-cloud', 'bs-sweep', 'bs-bamboo', 'bs-glide', 'bs-mist', 'bs-reed', 'bs-tempest'],
    basic: { power: 0.86, range: 2.1, rate: 0.65 },
  },
  {
    id: 'drift-fist',
    name: 'หมัดหมอกวน',
    school: 'ยอดหมอก',
    schoolId: 'mist-peak',
    learnCost: 250,
    requires: 'breeze-sword',
    weapon: 'unarmed',
    element: 'taiji',
    damage: 'internal',
    desc: 'ใช้ความอ่อนสยบความแข็ง ยืมแรงศัตรูคืนสู่ศัตรู ท่าทำลายชาร์จไวและมึนงงนาน',
    skills: ['tf-push', 'tf-grasp', 'tf-whip', 'tf-clouds', 'tf-yield', 'tf-step', 'tf-wheel', 'tf-supreme'],
    basic: { power: 1.11, range: 1.65, rate: 0.6 },
  },
  {
    id: 'drift-sword',
    name: 'กระบี่วงธารา',
    school: 'ยอดหมอก',
    schoolId: 'mist-peak',
    learnCost: 600,
    requires: 'drift-fist',
    weapon: 'sword',
    element: 'taiji',
    damage: 'internal',
    desc: 'วิชากระบี่สูงสุดของยอดหมอก ต้องเข้าใจหมัดหมอกวนก่อนจึงจะเรียนได้ กระบวนท่าเป็นวงกลมไม่ขาดสาย',
    skills: [
      'ts-circle',
      'ts-yinyang',
      'ts-stars',
      'ts-still',
      'ts-flow',
      'ts-thread',
      'ts-focus',
      'ts-heaven',
    ],
    basic: { power: 1.02, range: 2.0, rate: 0.8 },
  },
  {
    id: 'snow-sword',
    name: 'กระบี่ธารเยือกแข็ง',
    school: 'พเนจร',
    weapon: 'sword',
    element: 'soft',
    damage: 'external',
    learnCost: 400,
    desc: 'วิชากระบี่ธาตุอ่อน เย็นยะเยือกดุจหิมะ สะสม "เหน็บหนาว" จนศัตรูมึน ไล่ตามด้วยวิชาตัวเบา และปิดฉากด้วยเหมันต์สะกดธาร',
    skills: ['ss-shift', 'ss-frost', 'ss-gale', 'ss-walk', 'ss-ice', 'ss-pride', 'ss-trace', 'ss-hatred'],
    basic: { power: 1, range: 2.0, rate: 0.75 },
  },
  {
    id: 'bell-fist',
    name: 'หมัดระฆังก้อง',
    school: 'หินระฆัง',
    schoolId: 'bell-stone',
    learnCost: 0,
    weapon: 'unarmed',
    element: 'hard',
    damage: 'external',
    desc: 'วิชาหมัดของช่างหล่อระฆังแห่งหินระฆัง หนักแน่นดุจภูผา ทนทานดุจเนื้อระฆัง และสยบศัตรูด้วยเสียงก้อง',
    skills: ['ar-strike', 'ar-palm', 'ar-bell', 'ar-sweep', 'ar-break', 'ar-lion', 'ar-step', 'ar-buddha'],
    basic: { power: 0.9, range: 1.7, rate: 0.7 },
  },
  {
    id: 'lamp-hook-sabre',
    name: 'ดาบขอเกี่ยวโคม',
    school: 'ทางโคมเหล็ก',
    schoolId: 'lantern-road',
    learnCost: 0,
    weapon: 'sword',
    element: 'yang',
    damage: 'external',
    desc: 'ดาบขอเกี่ยวของทางโคมเหล็ก รวดเร็วและแม่นยำ ทำให้ศัตรูโลหิตไหลแล้วปิดฉากด้วยท่าเดียว',
    skills: [
      'jy-cut',
      'jy-cross',
      'jy-whirl',
      'jy-lunge',
      'jy-shatter',
      'jy-oath',
      'jy-execute',
      'jy-dragon',
    ],
    basic: { power: 1.05, range: 2.0, rate: 0.75 },
  },
  {
    id: 'quiet-palm',
    name: 'ฝ่ามือเงียบจันทร์มัว',
    school: 'จันทร์มัว',
    schoolId: 'dim-moon',
    learnCost: 0,
    weapon: 'unarmed',
    element: 'yin',
    damage: 'internal',
    desc: 'วิชาฝ่ามือธาตุหยินของหุบจันทร์มัว เงียบและเนิบ ใช้เสียงกล่อมให้ศัตรูชะงักและฟื้นพลังคืนแก่ตน',
    skills: ['jl-claw', 'jl-needle', 'jl-charm', 'jl-step', 'jl-soul', 'jl-drain', 'jl-bloom', 'jl-abyss'],
    basic: { power: 0.85, range: 1.7, rate: 0.65 },
  },
  {
    id: 'ink-slate',
    name: 'กระบี่แท่งหมึกสน',
    school: 'หอหมึกสน',
    schoolId: 'pine-ink',
    learnCost: 0,
    weapon: 'sword',
    element: 'soft',
    damage: 'internal',
    desc: 'วิชาของผู้จดบันทึกประลองที่ถือแท่งหมึกกับแผ่นศิลา แม่นยำ ปิดจุดชีพจร และพลิกเกมด้วยสมาธิ',
    skills: ['jz-stroke', 'jz-poem', 'jz-ink', 'jz-seal', 'jz-ward', 'jz-focus', 'jz-break', 'jz-scroll'],
    basic: { power: 0.95, range: 2.05, rate: 0.7 },
  },
  {
    id: 'heat-sabre',
    name: 'ดาบชาดคลั่งไฟ',
    school: 'ดาบชาด',
    schoolId: 'cinnabar',
    learnCost: 0,
    weapon: 'sword',
    element: 'yang',
    damage: 'external',
    desc: 'วิชาดาบใหญ่ของสำนักดาบชาด ตีดาบด้วยไฟ ฟันหนักให้ศัตรูเลือดไหล แล้วดื่มไฟนั้นหล่อเลี้ยงตน',
    skills: [
      'bs-slash',
      'bs-rend',
      'bs-whirl',
      'bs-charge',
      'bs-break',
      'bs-frenzy',
      'bs-drink',
      'bs-massacre',
    ],
    basic: { power: 1.15, range: 2.1, rate: 0.85 },
  },
  ...EXTRA_ARTS,
  DRAGON_PALM_ART_DEF,
  STAFF_ART_DEF,
];

/** The art a new character starts with (and a save of the hidden nine swords is moved to): กระบี่ไผ่ห่มหมอก, a plain sword art open to anyone, whose moves fit the starting sword and the hero's sprite. */
export const STARTER_ART = 'breeze-sword';

export const MARTIAL_ARTS: Readonly<Record<string, MartialArtDef>> = Object.fromEntries(
  arts.map((a) => [a.id, a]),
);

export function getArt(id: string): MartialArtDef {
  const a = MARTIAL_ARTS[id];
  if (!a) throw new Error(`Unknown martial art: ${id}`);
  return a;
}

/** Every Han glyph the UI may render, used to subset the display font. */
export const UI_GLYPHS = Array.from(
  new Set(
    [
      ...list.map((s) => s.glyph),
      ...'九劍守破攻輕坐功氣躍怒奔自人囊武門衣問設聲騎悔魔無敵殺牢地商令鎖初',
      ...ZHUMING_NAME.han,
      ...REALMS_TO_COME.map((r) => r.glyph),
      STATUS_GLYPHS,
      MOUNT_GLYPHS,
      LADDER_GLYPHS,
      ARENA_GLYPHS,
      ...Object.values(ELEMENT_INFO).map((e) => e.glyph),
    ].join(''),
  ),
).join('');

/** The art a move belongs to (enemy moves have none). */
const ART_OF_SKILL = new Map<string, MartialArtDef>();
for (const a of arts) for (const id of a.skills) ART_OF_SKILL.set(id, a);
// Wind-ups in data (duel audit 2026-10-11, COMBAT-FIX): every player lock move and power move is told before it lands (the
// other can answer it: a step aside, a counter stance), so the tell ring and its sound always show. A move that has one keeps it.
for (const a of arts)
  for (const id of a.skills) {
    const s = SKILLS[id] as { windup: number } & SkillDef;
    if (!s || s.windup > 0) continue;
    if (s.lockForm) s.windup = (s.hits ?? 1) >= 2 ? WINDUP_DATA.lockFlurry : WINDUP_DATA.lockSingle;
    else if (s.kind === 'power') s.windup = s.aoe || s.spin ? WINDUP_DATA.powerArea : WINDUP_DATA.powerSingle;
  }
export const artOfSkill = (skillId: string): MartialArtDef | undefined => ART_OF_SKILL.get(skillId);
